import { Logic } from '~common/ui-logic/logic'
import type { AppContextType } from '~/app-context'
import type { TaskState } from '../../services/cache/types'
import type {
    BankTransaction,
    Invoice,
    DocumentTransactionAssociation,
} from '~/storage/types'
import { executeTask } from '~common/ui-logic/tasks'
import { CacheService } from '~/services/cache/cache'
import type { DateRange } from 'react-day-picker'

interface MatchSearchDependencies {
    rootItem: BankTransaction | Invoice
    itemType: 'transaction' | 'invoice'
    title: string
}

interface MatchSearchState {
    currentTab?: 'invoices' | 'emails'
    loadState: TaskState
    searchTerm?: string
    dateRange?: DateRange
}

export class MatchSearchLogic extends Logic<
    MatchSearchDependencies & AppContextType,
    MatchSearchState
> {
    getInitialState = (): MatchSearchState => ({
        currentTab: undefined,
        loadState: 'pristine',
        searchTerm: undefined,
        dateRange: undefined,
    })

    shouldReload(
        oldDeps: MatchSearchDependencies & AppContextType,
        newDeps: MatchSearchDependencies & AppContextType,
    ): boolean {
        // Re-initialize if rootItem or itemType changes
        return (
            oldDeps.rootItem.id !== newDeps.rootItem.id ||
            oldDeps.itemType !== newDeps.itemType
        )
    }

    async initialize() {
        await executeTask(this, 'loadState', async () => {
            // Reset state when initializing with new props
            this.setState({
                loadState: 'success',
                searchTerm: undefined,
                dateRange: undefined,
            })

            if (this.deps.itemType === 'transaction') {
                this.setState({ currentTab: 'invoices' })
            } else {
                this.setState({ currentTab: undefined })
            }
        })
    }

    // Pure method to compute suggestedIds from cache - call from render
    static getSuggestedIds(
        rootItem: BankTransaction | Invoice,
        itemType: 'transaction' | 'invoice',
        cache: CacheService, // CacheService instance
    ): string[] {
        const associations =
            cache.getKey('documentTransactionAssociations') ||
            ({} as Record<string, DocumentTransactionAssociation>)
        let suggestedAssocIds: string[]

        if (itemType === 'transaction') {
            suggestedAssocIds =
                cache.getKey('matchesByTransaction')?.[rootItem.id as string] ||
                []
        } else {
            suggestedAssocIds =
                cache.getKey('matchesByInvoice')?.[rootItem.id as string] || []
        }

        const items = suggestedAssocIds
            .map((assocId) => {
                const assoc = associations[assocId]
                if (!assoc) return null
                const targetId =
                    itemType === 'transaction'
                        ? assoc.documentId
                        : assoc.transactionId
                return { assocId, assoc, targetId }
            })
            .filter(
                (
                    item,
                ): item is {
                    assocId: string
                    assoc: DocumentTransactionAssociation
                    targetId: string
                } => item !== null,
            )

        // Deduplicate by targetId, preferring user_confirmed associations
        const seenTargetIds = new Map<string, (typeof items)[0]>()
        for (const item of items) {
            const existing = seenTargetIds.get(item.targetId)
            if (!existing) {
                seenTargetIds.set(item.targetId, item)
            } else if (
                item.assoc.method === 'user_confirmed' &&
                existing.assoc.method !== 'user_confirmed'
            ) {
                // Replace with user_confirmed version
                seenTargetIds.set(item.targetId, item)
            }
        }

        const sorted = Array.from(seenTargetIds.values()).sort((a, b) => {
            // Sort user_confirmed matches first
            const aIsConfirmed = a.assoc.method === 'user_confirmed'
            const bIsConfirmed = b.assoc.method === 'user_confirmed'

            if (aIsConfirmed && !bIsConfirmed) {
                return -1
            }
            if (!aIsConfirmed && bIsConfirmed) {
                return 1
            }
            return 0
        })

        return sorted.map((item) => item.targetId)
    }

    setCurrentTab = (tab: 'invoices' | 'emails') => {
        this.setState({ currentTab: tab })
        if (tab === 'emails') {
            this.deps.services.globalEvents.emit({
                type: 'performSearch',
                context: 'match-search',
                searchTerm: this.state.searchTerm,
                dateRange: this.state.dateRange,
            })
        }
    }

    updateSearchTerm = (term: string, sendResults: boolean = false) => {
        this.setState({ searchTerm: term })
        if (sendResults || this.state.currentTab !== 'emails') {
            this.deps.services.globalEvents.emit({
                type: 'performSearch',
                context: 'match-search',
                searchTerm: term,
                dateRange: this.state.dateRange,
            })
        }
    }

    updateDateRange = (
        range: DateRange | undefined,
        sendResults: boolean = false,
    ) => {
        this.setState({ dateRange: range })
        if (
            range != null &&
            (sendResults || this.state.currentTab !== 'emails')
        ) {
            this.deps.services.globalEvents.emit({
                type: 'performSearch',
                context: 'match-search',
                dateRange: range,
                searchTerm: this.state.searchTerm,
            })
        }
    }

    confirmMatch = async (otherId: string) => {
        const user = await this.deps.storageModules.auth.getCurrentUser()
        if (!user) return

        const userId = user.id
        const documentId =
            this.deps.itemType === 'transaction'
                ? otherId
                : (this.deps.rootItem.id as string)
        const transactionId =
            this.deps.itemType === 'transaction'
                ? (this.deps.rootItem.id as string)
                : otherId

        await this.deps.services.transactionInvoiceMatching.confirmAssociation(
            documentId,
            transactionId,
        )

        // Close the pane
        this.deps.services.globalEvents.emit({
            type: 'closeCurrentPane',
        })
    }
}

export type { MatchSearchDependencies, MatchSearchState }
